import { AsyncPipe, JsonPipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { EntityPickerComponent } from 'entity-picker-lib';
import { IEntity, INamedEntity, IPickerInfo } from 'interfaces-lib';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { IdentityStorage } from 'storage-service-lib';
import { IBowlingAlleyService } from '../../services/bowling-alley.service';
import { BullhornIconService } from '../../services/bullhorn.icon.service';
import { IAddLinkedEntityRequest, ILinkedEntity, LinkedEntitiesService } from '../../services/linked-entities.service';
import { ServiceProviderService } from '../../services/service.provider.service';
import { IBullhornCardContext } from '../../types/bullhorn-card-context';
import { BullhornEntityType } from '../../types/bullhorn/bullhorn-entity-type';
import { ICall } from '../notetaker-card/notetaker-result/ICallInterface';

@Component({
  selector: 'app-linked-entities',
  templateUrl: './linked-entities.component.html',
  styleUrls: ['./linked-entities.component.scss'],
  standalone: true,
  imports: [EntityPickerComponent, MatIcon, MatButton]
})
export class LinkedEntitiesComponent implements OnInit, OnDestroy {
  @ViewChild('linkedEntitiesPopupContainer') popupContainer!: ElementRef;
  @Output() togglePopup = new EventEmitter<boolean>();
  @Input() bullhornCardContext!: IBullhornCardContext;
  @Input() currentCall!: ICall;

  searchTerm = '';
  entities: BehaviorSubject<ILinkedEntity[]> = new BehaviorSubject([]);
  isLoading = true;

  private clickListener!: (event: Event) => void;
  private readonly bowlingAlleyService: IBowlingAlleyService | undefined;

  constructor(
    private linkedEntitiesService: LinkedEntitiesService,
    private bullhornIconService: BullhornIconService,
    private identifyService: IdentityStorage,
    serviceProviderService: ServiceProviderService
  ) {
    this.bowlingAlleyService = serviceProviderService.getBowlingAlleyService('bullhorn');
  }

  ngOnInit(): void {
    this.clickListener = (event: Event) => {
      if (!this.popupContainer.nativeElement.contains(event.target)) {
        this.togglePopup.emit(false);
      }
    };
    document.addEventListener('click', this.clickListener);
    this.loadLinkedEntities();
  }

  private loadLinkedEntities(): void {
    if (this.bullhornCardContext) {
      this.linkedEntitiesService
        .getLinkedEntitiesForCall(this.bullhornCardContext.corporationId, this.currentCall?.callId)
        .subscribe({
          next: (entities) => {
            this.entities.next(entities);
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Error loading linked entities:', error);
            this.isLoading = false;
          },
        });
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.clickListener);
  }

  removeLinkedEntity(entity: ILinkedEntity, event?: Event): void {
    event?.stopPropagation();
    // Optimistic UI update - remove entity immediately
    const currentEntities = this.entities.value;
    const updatedEntities = currentEntities.filter(e => e.id !== entity.id || e.entityType !== entity.entityType);
    this.entities.next(updatedEntities);

    // Process server request in background
    this.linkedEntitiesService
      .deleteLinkedEntity(
        this.bullhornCardContext.corporationId,
        entity.entityType,
        entity.id,
        this.currentCall.callId
      )
      .subscribe( {
        next: () => {
          // Refresh to ensure server state consistency
          this.loadLinkedEntities();
        },
        error: (error) => {
          console.error('Error removing linked entity:', error);
          // Revert optimistic update on error
          this.entities.next(currentEntities);
        },
      });
  }

  // added loading
  // call clear all entities service
  clearAllLinkedEntities(): void {
    this.isLoading = true;
    this.linkedEntitiesService
      .clearAllLinkedEntities(this.bullhornCardContext.corporationId, this.currentCall.callId)
      .subscribe({
        next: () => {
          this.entities.next([]);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error removing linked entities:', error);
          this.isLoading = false;
        },
      });
  }

  addLinkedEntity(entityRequest: IEntity | INamedEntity): void {
    // Optimistic UI update - add entity immediately
    const optimisticEntity: ILinkedEntity = {
      id: entityRequest.id,
      name: (entityRequest as INamedEntity).name || `${entityRequest.type} ${entityRequest.id}`,
      namespace: this.identifyService.get().namespace,
      entityType: entityRequest.type
    };

    const currentEntities = this.entities.value;
    this.entities.next([...currentEntities, optimisticEntity]);

    // Process server request in background
    this.linkedEntitiesService
      .addLinkedEntity(
        this.bullhornCardContext.corporationId,
        entityRequest.type,
        entityRequest.id,
        this.currentCall.callId,
        this.identifyService.get().namespace,
        this.currentCall
      )
      .subscribe({
        next: (newCallId) => {
          this.currentCall.callId = newCallId;
          // Refresh to get server state (in case of any differences)
          this.loadLinkedEntities();
        },
        error: (error) => {
          console.error('Error adding linked entity:', error);
          // Revert optimistic update on error
          this.entities.next(currentEntities);
        },
      });
  }

  getEntityIcon(entityType: string): string {
    return this.bullhornIconService.getIcon(entityType as BullhornEntityType);
  }

  selectEntity(event: IPickerInfo) {
    this.addLinkedEntity(event.entity);
  }

  openEntityInBowlingAlley(entity: ILinkedEntity, event: Event): void {
    event.stopPropagation();
    this.bowlingAlleyService?.openEntity(entity.entityType, entity.id);
  }
}
